import './Privacy.css';

function Privacy() {
  return (
    <div className="privacy">
      <section className="privacy-hero">
        <div className="privacy-hero-img">
          <img src="/assets/hero_img.png" alt="Hero Image" />
        </div>
        <div className="container">
          <h1>Privacy & Security</h1>
          <p className="hero-subtitle">Your privacy and data security are our top priorities</p>
          <p className="last-updated">Last updated: February 21, 2026</p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="container">
          <div className="policy-section">
            <h2>Summary of Key Points</h2>
            <p>
              This privacy notice for Small Robot LLC describes how and why we
              might collect, store, use, and/or share your information when you use our mobile applications [TAP, EDICT!]
              or engage with us in other related ways.
            </p>
          </div>

          <div className="policy-section">
            <h2>Information We Collect</h2>
            <h3>Personal Information You Provide</h3>
            <p>We collect personal information that you voluntarily provide when you:</p>
            <ul>
              <li>Register on our mobile application</li>
              <li>Express interest in our products and services</li>
              <li>Participate in activities on our Services</li>
              <li>Contact us for support</li>
            </ul>
            <p>This may include:</p>
            <ul>
              <li>Names</li>
              <li>Phone numbers</li>
              <li>Email addresses</li>
              <li>Usernames</li>
            </ul>

            <h3>Information Automatically Collected</h3>
            <p>
              We automatically collect certain information when you visit, use, or navigate our Services,
              including device and usage information such as IP address, browser characteristics,
              operating system, and usage patterns.
            </p>

            <h3>Mobile Application Data</h3>
            <p>If you use our mobile application, we may collect:</p>
            <ul>
              <li><strong>Geolocation Information:</strong> We may request access to location-based information from your mobile device to provide location-based services</li>
              <li><strong>Mobile Device Access:</strong> We may request access to your device's calendar, camera, reminders, and SMS messages</li>
              <li><strong>Push Notifications:</strong> We may request to send you push notifications regarding your account or app features</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>App-Specific Data Practices</h2>

            <h3>TAP</h3>
            <ul>
              <li><strong>Geolocation:</strong> Required to show nearby TAPs and location-tagged hangouts in public spaces</li>
              <li><strong>Google Maps:</strong> Location data is shared with Google Maps Platform APIs to display maps and nearby venues</li>
              <li><strong>Device Access:</strong> May request access to camera (profile photos), calendar, reminders, and SMS for in-app features</li>
              <li><strong>Push Notifications:</strong> Used to alert you when someone joins your TAP or sends a message</li>
              <li><strong>Public Visibility:</strong> Your username and active TAP location are visible to other users while a TAP is live</li>
            </ul>

            <h3>EDICT</h3>
            <ul>
              <li><strong>Camera:</strong> Used for capturing and uploading impact photos within your dimension</li>
              <li><strong>Push Notifications:</strong> Used to notify you of responses to your edicts, breaking interdimensional news, and activity in your dimension</li>
              <li><strong>User-Generated Content:</strong> Edicts, journalism entries, and photos you post are visible to other users and may be featured within the app</li>
              <li><strong>No Location Data:</strong> EDICT does not collect or use your device's location</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>How We Process Your Information</h2>
            <p>We process your personal information for various reasons, including:</p>
            <ul>
              <li><strong>Account Management:</strong> To facilitate account creation, authentication, and user account management</li>
              <li><strong>Service Delivery:</strong> To provide you with our requested services</li>
              <li><strong>User Support:</strong> To respond to your inquiries and solve potential issues</li>
              <li><strong>User Communication:</strong> To enable user-to-user communications through our platform</li>
              <li><strong>Marketing:</strong> To send you marketing and promotional communications (with your consent)</li>
              <li><strong>Security:</strong> To protect our services through fraud monitoring and prevention</li>
              <li><strong>Service Improvement:</strong> To evaluate and improve our services and user experience</li>
              <li><strong>Legal Compliance:</strong> To comply with our legal obligations and protect our legal rights</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>When and With Whom We Share Information</h2>
            <p>We may share information in specific situations with the following parties:</p>
            <ul>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition</li>
              <li><strong>Google Maps Platform APIs (TAP only):</strong> We share location information with Google Maps APIs to power TAP's location-based features</li>
              <li><strong>Other Users:</strong> When you share information or interact with public areas of our Services, this information may be viewed by other users</li>
              <li><strong>Service Providers:</strong> With service providers who assist us in operating our services under written contracts</li>
            </ul>
            <p><strong>We do not sell your personal information to third parties.</strong></p>
          </div>

          <div className="policy-section">
            <h2>How We Keep Your Information Safe</h2>
            <p>
              We have implemented appropriate technical and organizational security measures designed to
              protect your personal information. However, no electronic transmission over the Internet
              or information storage technology can be guaranteed to be 100% secure. You should only
              access our Services within a secure environment.
            </p>
          </div>

          <div className="policy-section">
            <h2>Information from Minors</h2>
            <p>
              We do not knowingly collect data from or market to children under 18 years of age.
              By using our Services, you represent that you are at least 18 or have parental consent.
              If we learn that personal information from users under 18 has been collected, we will
              deactivate the account and delete such data.
            </p>
          </div>

          <div className="policy-section">
            <h2>How Long We Keep Your Information</h2>
            <p>
              We keep your personal information for as long as necessary to fulfill the purposes
              outlined in this privacy notice, unless a longer retention period is required by law.
              Generally, we retain information for as long as you have an account with us.
            </p>
          </div>

          <div className="policy-section">
            <h2>Your Privacy Rights</h2>
            <p>You may review, change, or terminate your account at any time. Your rights include:</p>
            <ul>
              <li><strong>Account Access:</strong> Log in to your account settings to update your information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
              <li><strong>Opt-out of Marketing:</strong> Unsubscribe from marketing communications by clicking unsubscribe links in emails</li>
              <li><strong>Data Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Data Access:</strong> Request access to your personal information</li>
            </ul>
            <p>For California residents, additional rights apply under the CCPA including the right to know about data collection and sharing practices.</p>
          </div>

          <div className="policy-section">
            <h2>Updates to This Notice</h2>
            <p>
              We may update this privacy notice from time to time. The updated version will be indicated
              by an updated "Revised" date and will be effective as soon as it is accessible. We encourage
              you to review this privacy notice frequently.
            </p>
          </div>

          <div className="policy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions or comments about this privacy notice, you may email us at:{' '}
              <a href="mailto:support@smallrobot.app">support@smallrobot.app</a>
            </p>
            <p>Or contact us by post at:</p>
            <p>
              Small Robot LLC<br />
              514 Gillis Street A<br />
              Kansas City, MO 64106<br />
              United States
            </p>
            <p>
              You can also{' '}
              <a href="https://app.termly.io/notify/8f4992be-bfee-4611-847c-dc9f0d31fd56" target="_blank" rel="noopener noreferrer">
                submit a data subject access request
              </a>{' '}
              to review, update, or delete your personal information.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Privacy;